import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatCurrency } from '@/lib/utils';
import { Loader2, TrendingUp, TrendingDown, Package, Calculator, Save } from 'lucide-react';
import { useProjectCostAnalysis, ProjectCostAnalysisItem } from '@/hooks/useProjectCostAnalysis';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface ProjectCostAnalysisModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectCostAnalysisModal: React.FC<ProjectCostAnalysisModalProps> = ({
  projectId,
  isOpen,
  onClose
}) => {
  const { data: costAnalysis, isLoading, error } = useProjectCostAnalysis(projectId);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<{[key: string]: string}>({});
  const [optimisticUpdates, setOptimisticUpdates] = useState<Record<string, { quantity?: number; unitCost?: number }>>({});
  
  const queryClient = useQueryClient();

  const saveActualCostsMutation = useMutation({
    mutationFn: async ({ itemId, field, value }: { itemId: string; field: 'quantity' | 'unitCost'; value: number }) => {
      const item = costAnalysis?.items.find(i => i.id === itemId);
      if (!item) return;

      // Get current values, using estimated as defaults
      const currentQuantity = item.actualQuantity ?? item.quantity;
      const currentUnitCost = item.actualUnitCost ?? (item.finalCost / item.quantity);
      
      // Update the specific field
      const actualQuantity = field === 'quantity' ? value : currentQuantity;
      const actualUnitCost = field === 'unitCost' ? value : currentUnitCost;
      const actualTotalCost = actualQuantity * actualUnitCost;

      // Check if record exists
      const { data: existing } = await supabase
        .from('project_actual_costs')
        .select('id')
        .eq('project_id', projectId)
        .eq('estimate_line_item_id', itemId)
        .maybeSingle();

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('project_actual_costs')
          .update({
            actual_quantity: actualQuantity,
            actual_unit_cost: actualUnitCost,
            actual_total_cost: actualTotalCost,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('project_actual_costs')
          .insert({
            project_id: projectId,
            estimate_line_item_id: itemId,
            component_type: item.componentType,
            component_id: item.estimateLineItemId || itemId,
            description: item.description,
            actual_quantity: actualQuantity,
            actual_unit_cost: actualUnitCost,
            actual_total_cost: actualTotalCost,
            recorded_by: (await supabase.auth.getUser()).data.user?.id
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-cost-analysis', projectId] });
      setOptimisticUpdates({});
      toast.success('Cost updated successfully');
    },
    onError: (error) => {
      // Revert optimistic updates on error
      setOptimisticUpdates({});
      toast.error('Failed to save actual costs');
      console.error('Error saving actual costs:', error);
    }
  });

  const handleCellClick = (itemId: string, field: 'quantity' | 'unitCost') => {
    const item = costAnalysis?.items.find(i => i.id === itemId);
    if (!item) return;

    const fieldKey = `${itemId}-${field}`;
    setEditingField(fieldKey);
    
    // Set default value from estimated if no actual exists
    let defaultValue: string;
    if (field === 'quantity') {
      defaultValue = (getDisplayValue(item, 'quantity')).toString();
    } else {
      defaultValue = (getDisplayValue(item, 'unitCost')).toFixed(2);
    }
    
    setEditingValues(prev => ({
      ...prev,
      [fieldKey]: defaultValue
    }));
  };

  const handleCellBlur = (itemId: string, field: 'quantity' | 'unitCost') => {
    const fieldKey = `${itemId}-${field}`;
    const value = parseFloat(editingValues[fieldKey]);
    
    if (!isNaN(value) && value >= 0) {
      // Apply optimistic update immediately
      setOptimisticUpdates(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          [field]: value
        }
      }));
      
      saveActualCostsMutation.mutate({ itemId, field, value });
    }
    
    setEditingField(null);
    setEditingValues(prev => {
      const newValues = { ...prev };
      delete newValues[fieldKey];
      return newValues;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent, itemId: string, field: 'quantity' | 'unitCost') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCellBlur(itemId, field);
    }
    if (e.key === 'Escape') {
      setEditingField(null);
      setEditingValues(prev => {
        const newValues = { ...prev };
        delete newValues[`${itemId}-${field}`];
        return newValues;
      });
    }
  };

  // Get display value with optimistic updates and fallback to estimated
  const getDisplayValue = (item: any, field: 'quantity' | 'unitCost') => {
    const optimistic = optimisticUpdates[item.id]?.[field];
    if (optimistic !== undefined) return optimistic;
    
    if (field === 'quantity') {
      return item.actualQuantity ?? item.quantity;
    } else {
      return item.actualUnitCost ?? (item.finalCost / item.quantity);
    }
  };

  const getComponentTypeBadge = (type: string) => {
    const config = {
      supplier_line_item: { label: 'Supplier', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
      internal_line_item: { label: 'Internal', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      assembly: { label: 'Assembly', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' }
    };
    
    const { label, className } = config[type as keyof typeof config] || { label: type, className: 'bg-gray-100 text-gray-800' };
    
    return <Badge className={className}>{label}</Badge>;
  };

  const getVarianceBadge = (variance: number, percentage: number) => {
    const isPositive = variance > 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const colorClass = isPositive 
      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
      : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    
    return (
      <Badge className={colorClass}>
        <Icon className="h-3 w-3 mr-1" />
        {isPositive ? '+' : ''}{percentage.toFixed(2)}%
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading cost analysis...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !costAnalysis) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {error?.message || 'Failed to load cost analysis'}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Project Cost Analysis - Estimated vs Actual
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="summary" className="space-y-4">
          <TabsList>
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="by-category">By Category</TabsTrigger>
            <TabsTrigger value="all-items">All Items</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Estimated Total</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    R{costAnalysis.totalFinalCost.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Base: R{costAnalysis.totalBaseCost.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Actual Total</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    R{costAnalysis.totalActualCost.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Recorded costs
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Variance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    R{costAnalysis.totalVariance.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="flex items-center gap-2">
                    {getVarianceBadge(costAnalysis.totalVariance, costAnalysis.totalVariancePercentage)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Margin</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {costAnalysis.overallMargin.toFixed(2)}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Markup: {costAnalysis.overallMarkup.toFixed(2)}%
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="by-category" className="space-y-4">
            <ScrollArea className="h-[500px]">
              <div className="space-y-4">
                {costAnalysis.categorySummaries.map((category) => (
                  <Card key={category.categoryName}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{category.categoryName}</span>
                        <Badge variant="outline">{category.itemCount} items</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Estimated</p>
                          <p className="font-semibold">R{category.totalFinalCost.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Actual</p>
                          <p className="font-semibold">R{category.totalActualCost.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Variance</p>
                          <div className="flex items-center gap-2">
                            {getVarianceBadge(category.variance, category.variancePercentage)}
                          </div>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Margin</p>
                          <p className="font-semibold">{category.averageMargin.toFixed(2)}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="all-items" className="space-y-4">
            <ScrollArea className="h-[500px]">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1200px]">
                  <thead className="sticky top-0 bg-background border-b">
                    <tr className="text-left">
                      <th className="p-2 font-medium min-w-[200px]">Description</th>
                      <th className="p-2 font-medium min-w-[100px]">Type</th>
                      <th className="p-2 font-medium min-w-[100px]">Category</th>
                      <th className="p-2 font-medium min-w-[100px]">Est. Qty</th>
                      <th className="p-2 font-medium min-w-[120px]">Est. Cost</th>
                      <th className="p-2 font-medium min-w-[100px]">Act. Qty</th>
                      <th className="p-2 font-medium min-w-[120px]">Act. Cost</th>
                      <th className="p-2 font-medium min-w-[100px]">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costAnalysis.items.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-muted/50">
                        <td className="p-2">
                          <div className="font-medium">{item.description}</div>
                        </td>
                        <td className="p-2">
                          {getComponentTypeBadge(item.componentType)}
                        </td>
                        <td className="p-2 text-sm text-muted-foreground">{item.category}</td>
                        <td className="p-2 text-sm">{item.quantity.toFixed(2)}</td>
                        <td className="p-2">
                          <div className="text-sm">
                            <div>{formatCurrency(item.finalCost)}</div>
                            <div className="text-muted-foreground">
                              Unit: {formatCurrency(item.finalCost / item.quantity)}
                            </div>
                          </div>
                        </td>
                        <td className="p-2">
                          {editingField === `${item.id}-quantity` ? (
                            <Input
                              type="number"
                              value={editingValues[`${item.id}-quantity`] || ''}
                              onChange={(e) => setEditingValues(prev => ({
                                ...prev,
                                [`${item.id}-quantity`]: e.target.value
                              }))}
                              onBlur={() => handleCellBlur(item.id, 'quantity')}
                              onKeyDown={(e) => handleKeyDown(e, item.id, 'quantity')}
                              className="w-20 text-right border-0 bg-transparent p-0 focus:ring-1 focus:ring-primary [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&[type=number]]:appearance-textfield"
                              step="0.01"
                              autoFocus
                            />
                          ) : (
                            <div 
                              className="text-sm cursor-pointer hover:bg-muted p-1 rounded transition-colors"
                              onClick={() => handleCellClick(item.id, 'quantity')}
                            >
                              {getDisplayValue(item, 'quantity').toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          {editingField === `${item.id}-unitCost` ? (
                            <div className="space-y-2">
                              <Input
                                type="number"
                                value={editingValues[`${item.id}-unitCost`] || ''}
                                onChange={(e) => setEditingValues(prev => ({
                                  ...prev,
                                  [`${item.id}-unitCost`]: e.target.value
                                }))}
                                onBlur={() => handleCellBlur(item.id, 'unitCost')}
                                onKeyDown={(e) => handleKeyDown(e, item.id, 'unitCost')}
                                className="w-24 text-right border-0 bg-transparent p-0 focus:ring-1 focus:ring-primary [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&[type=number]]:appearance-textfield"
                                step="0.01"
                                placeholder="Unit cost"
                                autoFocus
                              />
                              <div className="text-xs text-muted-foreground">
                                Total: {formatCurrency(getDisplayValue(item, 'quantity') * 
                                          (parseFloat(editingValues[`${item.id}-unitCost`]) || 0))}
                              </div>
                            </div>
                          ) : (
                            <div 
                              className="text-sm cursor-pointer hover:bg-muted p-1 rounded space-y-1 transition-colors"
                              onClick={() => handleCellClick(item.id, 'unitCost')}
                            >
                              <div>{formatCurrency(getDisplayValue(item, 'quantity') * getDisplayValue(item, 'unitCost'))}</div>
                              <div className="text-muted-foreground">
                                Unit: {formatCurrency(getDisplayValue(item, 'unitCost'))}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          {(() => {
                            const actualTotal = getDisplayValue(item, 'quantity') * getDisplayValue(item, 'unitCost');
                            const variance = actualTotal - item.finalCost;
                            const variancePercentage = item.finalCost > 0 ? (variance / item.finalCost) * 100 : 0;
                            return getVarianceBadge(variance, variancePercentage);
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectCostAnalysisModal;